import { createContext, useContext, type ReactNode } from 'react';
import type {
  CityValue,
  TripWizardConfig,
} from '@koob/ui/src/features/tripWizard/types';

export interface TripDesignerConfigApi {
  getTrip: (params: {
    tripId: string;
    versionId?: string;
  }) => Promise<{ data: unknown }>;
  getExperience: (params: {
    experienceId: string;
    locale?: string;
  }) => Promise<{ data: unknown }>;
  getHotel: (params: {
    hotelId: string;
    locale?: string;
  }) => Promise<{ data: unknown }>;
  getRoomsAvailability: (params: {
    id: string;
    filters: unknown;
    locale?: string;
  }) => Promise<{ data: unknown }>;
  getHotelsAvailability: (params: {
    filters: unknown;
    locale?: string;
  }) => Promise<{ data: unknown }>;
  // Prices one experience instance (and lists its extras) for a party and a
  // date range.
  getExperiencesPricing: (params: {
    filters: unknown;
    locale?: string;
  }) => Promise<{ data: unknown }>;
  // Saves a trip as is: the version switch saves the trip it just loaded with
  // another `currentVersion`.
  saveTrip: (trip: unknown) => Promise<unknown>;
  // Filter bar lists. Each returns `{ data: [{ id, displayName }] }`.
  getThemes: () => Promise<{ data: unknown }>;
  getSegments: () => Promise<{ data: unknown }>;
  getCategories: () => Promise<{ data: unknown }>;
  getKinds: () => Promise<{ data: unknown }>;
  getStyles: (params?: { locale?: string }) => Promise<{ data: unknown }>;
  getSustainableLevels: () => Promise<{ data: unknown }>;
  // The connection between a TO and a DMC for one product kind; its first
  // entry carries the currency the filter bar prices in.
  getOrganizationByToDmc: (params: {
    toOrganizationId: number;
    dmcOrganizationId: unknown;
    kind: string;
  }) => Promise<{ data: unknown }>;
}

// What the design-trip wizard asks of the app. The package owns the form, the
// save and the trip filters it leaves behind; the app owns who the DMC is,
// which countries and cities it offers, and where the user goes next.
export interface DesignTripHost {
  // The app's `tripWizard` translations.
  t: (key: string, options?: Record<string, unknown>) => string;
  // `fixed`: the DMC is the logged-in organisation. `select`: the TO picks one.
  dmc: TripWizardConfig['dmc'];
  loadCountries: TripWizardConfig['loadCountries'];
  loadCities: (query: string, locale: string) => Promise<CityValue[]>;
  // When set, the name step asks for the trip language (DMC).
  localeOptions?: TripWizardConfig['localeOptions'];
  // The brief is the TO's ask: the DMC only reads it.
  briefReadOnly?: boolean;
  // Runs once a trip is saved and the filters are in the store.
  afterSave: (result: { tripId: string; locale: string }) => void;
  // Edit mode: grow or trim the composed days to the new duration (TO). DMC
  // leaves it unset and keeps its days.
  syncDaysWithDuration?: boolean;
  // Saving replaces the store's trip filters instead of merging into them
  // (DMC). TO merges: its filters carry the client folder.
  replacesTripFilters?: boolean;
  // Shown in place of the wizard (DMC group view: pick the DMC first).
  renderBlocked?: (onExit?: () => void) => ReactNode | null;
}

export interface HotelPriceOverride {
  // Per night: what the day's hotel line shows.
  price?: number;
  // The whole stay: what the trip total counts, once per stay.
  stayPrice?: number;
  currency?: string;
}

export interface ExperiencePriceOverride {
  price?: number;
  currency?: string;
}

export interface TripDesignerConfig {
  api: TripDesignerConfigApi;
  isOwnProduct: (product: unknown) => boolean;
  getFactsheetUrl: (experience: unknown) => string | null;
  locale: string;
  onEvent?: (name: string, payload: unknown) => void;
  // DMC-only: `hotel.previousBooking` wins over the day's live hotel price in
  // useTripTotal. TO passes nothing and gets the plain day/hotel price.
  getHotelPriceOverride?: (hotel: unknown) => HotelPriceOverride | undefined;
  // DMC-only: `experience.previousBooking` wins over the freshly fetched
  // price in useTripExperiencePricing, netted against whichever of `extras`
  // it already booked and confirmed. TO passes nothing and gets the plain
  // fetched price plus every selected extra.
  getExperiencePriceOverride?: (
    experience: unknown,
    extras: unknown[],
  ) => ExperiencePriceOverride | undefined;
  // DMC-only: fields merged into every experience pricing request (which
  // trip it is priced for, whether the template price applies, partner
  // programs). TO passes nothing and sends the plain request.
  getExperiencePricingFilters?: (
    tripFilters: any,
  ) => Record<string, unknown> | undefined;
  // DMC-only: `day.experiencePricing` keeps the raw pricing response (with the
  // program's extras) and the date it was priced for, and a day whose stored
  // response still matches its dates does not ask again. TO leaves it unset:
  // there `day.experiencePricing` carries the priced total instead.
  storesExperiencePricingResponse?: boolean;
  // DMC-only: the extras a trip was saved with stay as saved. They are not
  // repriced from the program's fresh price, and are not dropped when it no
  // longer lists them (a DMC designs the offer, it does not resell it). TO
  // leaves it unset: there a loaded extra is repriced, or dropped with a
  // notice when it is no longer offered.
  keepsSavedExtras?: boolean;
  // The design-trip wizard's host; unset where no wizard is mounted.
  designTrip?: DesignTripHost;
}

// isOwnProduct defaults to "yes" — only DMC's partner-marketplace rules say
// otherwise, so only DMC's wrapper overrides it.
type TripDesignerConfigInput = Omit<TripDesignerConfig, 'isOwnProduct'> & {
  isOwnProduct?: TripDesignerConfig['isOwnProduct'];
};

const TripDesignerConfigContext = createContext<TripDesignerConfig | null>(
  null,
);

export function TripDesignerConfigProvider({
  config,
  children,
}: {
  config: TripDesignerConfigInput;
  children: ReactNode;
}) {
  const value: TripDesignerConfig = {
    ...config,
    isOwnProduct: config.isOwnProduct ?? (() => true),
  };

  return (
    <TripDesignerConfigContext.Provider value={value}>
      {children}
    </TripDesignerConfigContext.Provider>
  );
}

export function useTripDesignerConfig(): TripDesignerConfig {
  const config = useContext(TripDesignerConfigContext);

  if (!config) {
    throw new Error(
      'useTripDesignerConfig must be used within a TripDesignerConfigProvider',
    );
  }

  return config;
}
